import { http } from './http';
import type { HttpResponse } from './http';
import type { ProfileData, UnsubscribeRequest } from './types/profile';
import type { Invoice } from './types/invoice';
import type { ExtraSupport, ExtraSupportResponse, DeactivateRequest } from './types/order';
import type { Upgrade, ProductUpgradeInfo } from './types/upgrade';
import type { CheckoutInfoResponse, CheckoutRequest, CheckoutResponse } from './types/checkout';
import type {
    CheckoutCreateRequest,
    CheckoutCreateResult,
    PayPalCreateOrderResponse,
    PayPalCaptureOrderResponse,
} from './cart.service';
import type { DownloadResponse, Download } from './types/download';
import type {
    DiscountInfo,
    DiscountInfoResponse,
    DiscountToken,
    DiscountTokenResponse,
    GenerateTokenResponse,
    GenerateTokenInfo,
} from './types/discount';
import { accountService } from './account.service';
import { secureServer } from './AppSettings';

export const clientService = {
    emptyProfile,
    getProfile,
    updateProfile,
    updatePassword,
    getOrders,
    getInvoice,
    getExtraSupport,
    deactivateLicense,
    getUpgrades,
    getUpgradeInfo,
    getCheckoutInfo,
    getDownloads,
    getDiscountInfo,
    generateToken,
    checkout,
    createCheckout,
    createPayPalOrder,
    capturePayPalOrder,
    unsubscribe,
};

// const sleep = (delay) => new Promise((resolve) => setTimeout(resolve, delay));

function emptyProfile(): ProfileData {
    return {
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        address: '',
        city: '',
        state: '',
        country: '',
        zip: '',
        phone: '',
        fax: '',
        notify: false,
    };
}

async function getProfile(clientId: number): Promise<HttpResponse<ProfileData>> {
    const result = await http<ProfileData>({
        path: `/clients/${clientId}/profile`,
    });
    return result;
}

async function updateProfile(clientId: number, data: ProfileData): Promise<HttpResponse<any>> {
    const result = await http<any, ProfileData>({
        path: `/clients/${clientId}/profile`,
        method: 'PUT',
        body: data,
    });
    return result;
}

export interface UpdatePasswordRequest {
    oldPassword: string;
    newPassword: string;
}

async function updatePassword(clientId: number, data: UpdatePasswordRequest): Promise<HttpResponse<any>> {
    const result = await http<any, UpdatePasswordRequest>({
        path: `/clients/${clientId}/password`,
        method: 'POST',
        body: data,
    });
    return result;
}

interface OrderDataResponse {
    orderId: string;
    dateTime: string;
    type: string;
    amount: number;
    amountCAD: number;
    refundStatus: string;
    productName: string;
    product: string;
    curVer: string;
    productId: number;
    expiryDate: string;
    lifetimeSupport: boolean;
}

export interface OrderData {
    orderId: string;
    dateTime: Date;
    type: string;
    amount: number;
    amountCAD: number;
    refundStatus: string;
    productName: string;
    product: string;
    curVer: string;
    productId: number;
    expiryDate?: Date;
    lifetimeSupport: boolean;
}

const mapOrderData = (order: OrderDataResponse): OrderData => ({
    ...order,
    dateTime: new Date(order.dateTime),
    expiryDate: order['expiryDate'] === undefined ? null : new Date(order.expiryDate),
});

async function getOrders(clientId: number): Promise<HttpResponse<OrderData[]>> {
    const result = await http<OrderDataResponse[]>({
        path: `/clients/${clientId}/orders`,
    });
    if (result.ok && result.body) {
        return { ok: result.ok, body: result.body.map(mapOrderData) };
    } else {
        return { ok: false, message: result.message, body: null };
    }
}

// The API omits null fields but passes empty strings through (e.g. taxName for a
// taxed order with a non-Canadian billing address). A '' rendered by an
// `{invoice.field && ...}` guard becomes a bare text node outside <Text> and
// crashes @react-pdf, so blank fields become null at the data boundary.
const mapInvoice = (invoice: Invoice): Invoice => {
    const record = invoice as any;
    for (const key of Object.keys(record)) {
        if (record[key] === '') {
            record[key] = null;
        }
    }
    return invoice;
};

async function getInvoice(orderId: string): Promise<Invoice | null> {
    const result = await http<Invoice>({
        path: `/clients/invoices/${orderId}`,
    });
    if (result.ok && result.body) {
        return mapInvoice(result.body);
    } else {
        return null;
    }
}

const mapExtraSupport = (data: ExtraSupportResponse): ExtraSupport => ({
    ...data,
    dateTime: new Date(data.dateTime),
    expiryDate: new Date(data.expiryDate),
});

async function getExtraSupport(orderId: string): Promise<HttpResponse<ExtraSupport>> {
    const result = await http<ExtraSupportResponse>({
        path: `/clients/extra-support/${orderId}`,
    });

    if (result.ok && result.body) {
        return { ok: result.ok, message: result.message, body: mapExtraSupport(result.body) };
    } else {
        return { ok: false, message: result.message, body: null };
    }
}

async function deactivateLicense(
    orderId: string,
    productId: number,
    productName: string,
    reason: string
): Promise<boolean> {
    const result = await http<any, DeactivateRequest>({
        path: `/clients/${accountService.getAccount().id}/deactivate`,
        method: 'POST',
        body: {
            clientName: accountService.getAccount().name,
            clientEmail: accountService.getAccount().email,
            orderId: orderId,
            productId: productId,
            productName: productName,
            reason: reason,
        },
    });
    return result.ok;
}

async function unsubscribe(email: string): Promise<HttpResponse<any>> {
    const result = await http<any, UnsubscribeRequest>({
        path: `/clients/unsubscribe`,
        method: 'POST',
        body: {
            email: email,
        },
    });
    return result;
}

async function getUpgrades(): Promise<Upgrade[]> {
    const result = await http<Upgrade[]>({
        path: `/clients/${accountService.getAccount().id}/upgrades`,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return [];
    }
}

async function getUpgradeInfo(token: string): Promise<HttpResponse<ProductUpgradeInfo>> {
    const result = await http<ProductUpgradeInfo>({
        path: `/clients/upgrade/${token}`,
    });
    return result;
}

async function getCheckoutInfo(
    productId: number,
    quantity: number,
    license: number,
    orderId: number,
    isUpgrade: boolean,
    upgradeFromProduct: number
): Promise<HttpResponse<CheckoutInfoResponse>> {
    const result = await http<CheckoutInfoResponse>({
        path: `/clients/checkout?productId=${productId}&quantity=${quantity}&license=${license}&orderId=${orderId}&isUpgrade=${isUpgrade}&upgradeFromProduct=${upgradeFromProduct}`,
    });
    return result;
}

async function checkout(params: CheckoutRequest): Promise<CheckoutResponse | null> {
    const result = await http<CheckoutResponse, CheckoutRequest>({
        path: `${secureServer}/OrderProcessBackend.aspx`,
        method: 'POST',
        body: params,
        formData: true,
        skipAuth: true,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return null;
    }
}

// Authenticated counterpart of cartService.createCheckout. Posts to
// /clients/checkout (auth-gated) so the server can trust the JWT-derived
// ClientId and run upgrade/support price lookups server-side.
async function createCheckout(params: CheckoutCreateRequest): Promise<HttpResponse<CheckoutCreateResult>> {
    return await http<CheckoutCreateResult, CheckoutCreateRequest>({
        path: `/clients/checkout`,
        method: 'POST',
        body: params,
    });
}

// Authenticated counterpart of cartService.createPayPalOrder. Returns the raw
// HttpResponse so callers can surface the server's real failure reason
// (result.message) instead of a generic dialog string.
async function createPayPalOrder(params: CheckoutCreateRequest): Promise<HttpResponse<PayPalCreateOrderResponse>> {
    return await http<PayPalCreateOrderResponse, CheckoutCreateRequest>({
        path: `/clients/paypal/create-order`,
        method: 'POST',
        body: params,
    });
}

// Authenticated counterpart of cartService.capturePayPalOrder. Returns the raw
// HttpResponse so callers can surface the server's real failure reason
// (result.message) instead of a generic dialog string.
async function capturePayPalOrder(
    payPalOrderId: string,
    checkoutToken: string
): Promise<HttpResponse<PayPalCaptureOrderResponse>> {
    return await http<PayPalCaptureOrderResponse, { payPalOrderId: string; checkoutToken: string }>({
        path: `/clients/paypal/capture-order`,
        method: 'POST',
        body: { payPalOrderId, checkoutToken },
    });
}

const mapDownloads = (download: DownloadResponse): Download => ({
    ...download,
    dateTime: new Date(download.dateTime),
});

async function getDownloads(orderId: string): Promise<HttpResponse<Download>> {
    const result = await http<DownloadResponse>({
        path: `/clients/downloads/${orderId}`,
    });

    if (result.ok && result.body) {
        return { ok: result.ok, message: result.message, body: mapDownloads(result.body) };
    } else {
        return { ok: false, message: result.message, body: null };
    }
}

const mapDiscountToken = (token: DiscountTokenResponse): DiscountToken => ({
    ...token,
    dateTime: new Date(token.dateTime),
});

const mapDiscountInfo = (discountInfo: DiscountInfoResponse): DiscountInfo => ({
    volumeDiscounts: discountInfo.volumeDiscounts,
    discountTokens: discountInfo.discountTokens.map(mapDiscountToken),
    eligibleDiscount: discountInfo.eligibleDiscount,
});

async function getDiscountInfo(): Promise<DiscountInfo | null> {
    const result = await http<DiscountInfoResponse>({
        path: `/clients/${accountService.getAccount().id}/discounts`,
    });
    if (result.ok && result.body) {
        return mapDiscountInfo(result.body);
    } else {
        return null;
    }
}

const mapGenerateTokenResponse = (response: GenerateTokenResponse): GenerateTokenInfo => ({
    ...response,
    tokens: response.tokens.map(mapDiscountToken),
});

async function generateToken(): Promise<GenerateTokenInfo | null> {
    const result = await http<GenerateTokenResponse>({
        path: `/clients/${accountService.getAccount().id}/discounts/generate`,
    });
    if (result.ok && result.body) {
        return mapGenerateTokenResponse(result.body);
    } else {
        return null;
    }
}
